import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { TeamExpiryBanner } from '@components/Team/TeamExpiryBanner.web';
import theme from '@themes/theme';

interface ProjectDetailBannerProps {
  daysRemaining: number;
  onRemindLater: () => void;
  onUpgradeNow: () => void;
  isOwner?: boolean;
  isOwnerOrAdmin?: boolean;
  expiredDate?: Date;
  planName: string;
  teamId?: string;
  teamOwnerId?: string;
  teamOwnerName?: string;
}

export const ProjectDetailBanner: React.FC<ProjectDetailBannerProps> = ({
  daysRemaining,
  onRemindLater,
  onUpgradeNow,
  isOwner,
  isOwnerOrAdmin,
  expiredDate,
  planName,
  teamId,
  teamOwnerId,
  teamOwnerName,
}) => {
  const { t } = useTranslation();
  const showDaysRemaining = daysRemaining > 0 && daysRemaining <= 7;
  const isExpired = daysRemaining <= 0;
  if (!showDaysRemaining && isExpired) {
    return (
      <TeamExpiryBanner
        planName={planName}
        expiredDate={expiredDate ?? undefined}
        onUpgradeNow={onUpgradeNow}
        isOwnerOrAdmin={isOwnerOrAdmin}
        teamId={teamId}
        teamOwnerId={teamOwnerId}
        teamOwnerName={teamOwnerName}
      />
    );
  }
  return (
    <>
      {showDaysRemaining && (
        <Box style={styles.container}>
          <Box style={styles.leftContent}>
            <Box style={styles.iconContainer}>
              <Icon name='CalendarClock' height={42} width={42} />
            </Box>
            <Box>
              <Text variant='labelLargeWeb'>
                {'Your trial ends in '}
                <Text color='alertRed' variant='labelLargeWeb'>
                  {daysRemaining}
                </Text>
                {' days'}
              </Text>
              <Text variant='webSmall'>
                Upgrade to a Team plan to keep using advanced projects features.
              </Text>
            </Box>
          </Box>
          <Box style={styles.buttonContainer}>
            <Button
              isSmall
              marginTop='l'
              marginRight='xs'
              borderWidth={1}
              backgroundColor='white'
              borderColor='black'
              borderRadius='xs'
              variant='textweb'
              onPress={onRemindLater}
              accessibilityLabel={t('shared:remind')}>
              {t('shared:remind')}
            </Button>

            {isOwner && (
              <Button
                isSmall
                color='white'
                disabled={false}
                backgroundColor='black'
                variant='webprimary'
                marginTop='l'
                accessibilityLabel='Upgrade'
                onPress={onUpgradeNow}>
                Upgrade Now
              </Button>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.yellowVividMild,
    borderRadius: theme.spacing.s,
    paddingHorizontal: theme.spacing.l,
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: theme.colors.grey03,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: theme.spacing.m,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    minHeight: 80,
  },
  leftContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    marginRight: theme.spacing.s,
  },
  buttonContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.l,
    justifyContent: 'flex-end',
  },
});
